import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';

import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { cn } from 'lib/utils';

/**
 * The tool's one app client (Client ID, optional Client Secret), set by whoever configures
 * the tool. Everyone signs in through it with their own account. Editors see saved values
 * masked as the server sends them; a value left as shown, or emptied, keeps what is saved.
 * Below it, the owner's setting "Allow users to use their own client" (form field
 * `allowUserClient`); with it on, the tool's client is optional.
 */
export function ToolClientFields({
  disabled = false,
  errors = {},
}: Readonly<{ disabled?: boolean; errors?: Record<string, string> }>) {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const [showSecret, setShowSecret] = useState(false);
  const allowUserClient = !!watch('allowUserClient');

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="auto-clientId" className={cn('mb-2', errors.clientId && 'text-destructive')}>
            {t('CLIENT_ID') || 'Client ID'} {!allowUserClient && <span className="text-destructive">*</span>}
          </Label>
          <Controller
            name="clientId"
            control={control}
            render={({ field }) => (
              <Input
                id="auto-clientId"
                placeholder={t('ENTER_CLIENT_ID') || 'Enter client ID'}
                autoComplete="off"
                {...field}
                value={field.value ?? ''}
                disabled={disabled}
                className={errors.clientId ? 'border-destructive' : ''}
              />
            )}
          />
          {errors.clientId && <span className="text-xs text-destructive">{errors.clientId}</span>}
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="auto-clientSecret" className="mb-2">
            {t('CLIENT_SECRET') || 'Client Secret'}
          </Label>
          <Controller
            name="clientSecret"
            control={control}
            render={({ field }) => (
              <div className="relative w-full min-w-0">
                <Input
                  id="auto-clientSecret"
                  type={showSecret ? 'text' : 'password'}
                  placeholder={t('ENTER_CLIENT_SECRET') || 'Enter client secret'}
                  autoComplete="new-password"
                  className="!h-11 pr-10"
                  {...field}
                  value={field.value ?? ''}
                  disabled={disabled}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  disabled={disabled}
                  aria-label={
                    showSecret ? t('HIDE_PASSWORD') || 'Hide password' : t('SHOW_PASSWORD') || 'Show password'
                  }
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                >
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            )}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {t(
          'TOOL_CLIENT_SIGN_IN_HINT',
          'Everyone who uses this tool signs in with their own account through this app client.'
        )}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <Controller
          name="allowUserClient"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="auto-allowUserClient"
              checked={!!field.value}
              disabled={disabled}
              onCheckedChange={(checked) => field.onChange(checked === true)}
            />
          )}
        />
        <Label htmlFor="auto-allowUserClient" className="cursor-pointer">
          {t('ALLOW_USER_CLIENT', 'Allow users to use their own client')}
        </Label>
      </div>
      <p className="text-xs text-muted-foreground">
        {t(
          'ALLOW_USER_CLIENT_HINT',
          'Users may sign in through their own app client instead of this one; with this on, the client above is optional.'
        )}
      </p>
    </div>
  );
}
